<script setup>
import { ref } from 'vue';

/**
 * Contextual popup menu anchored to a trigger element with imperative show/hide control.
 *
 * @status recommended
 * @group actions
 * @useWhen Showing a list of contextual actions in a floating panel anchored to a trigger element
 * @interfaceShape imperative
 * @see https://quasar.dev/vue-components/menu Quasar q-menu API
 */
defineOptions({ name: 'HrMenu' });

const qMenuRef = ref(null);

defineExpose({
  /** Shows the menu. */
  show: () => qMenuRef.value?.show(),
  /** Hides the menu. */
  hide: () => qMenuRef.value?.hide(),
  /**
   * Re-measures the panel and places it again. The position engine measures once as the panel
   * opens and does not watch it afterwards, so call this whenever the contents change size while
   * it is open — otherwise the panel keeps the placement it was given when it was a different size.
   */
  updatePosition: () => qMenuRef.value?.updatePosition(),
});
</script>

<template>
  <q-menu
    ref="qMenuRef"
    class="hr-menu"
    v-bind="$attrs"
  >
    <!-- @slot Menu content, typically a list of HrMenuItem components. -->
    <slot />
  </q-menu>
</template>

<style lang="scss">

/*
 * Not scoped — Quasar teleports the menu panel outside the component DOM,
 * so scoped styles cannot reach it. The [data-theme] guard ensures these
 * rules only apply when a design system theme is active.
 */

[data-theme] .hr-menu {
  background: var(--app-surface-container);
  box-shadow: var(--app-elevation-2);
}

/*
 * Unguarded by [data-theme] on purpose — this corrects Quasar, which misbehaves in both themes.
 *
 * The position engine measures the panel once as it opens and writes the result back as an inline
 * max-width. A panel anchored near a viewport edge ends up pinned narrower than its own content, so
 * labels wrap with half the screen still empty. min-width resolves above max-width in the cascade,
 * whatever wrote it, and max-content is the width the panel's own content asks for — so the panel
 * is never squeezed below it, and menus short enough to fit are left alone.
 */
.hr-menu {
  min-width: max-content;
}

</style>
